import apiClient from "./apiClient";

/**
 * Fetch Company Basic Profile
 */
export const getCompanyProfile = async (companyCode) => {
  try {
    const res = await apiClient.get(`/api/auth/company/${companyCode}`, {
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[OrganizationService] getCompanyProfile error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Fetch Full Company Settings (Invoice, Branding, Call limits, App, etc.)
 */
export const getCompanySettings = async (companyCode) => {
  try {
    const res = await apiClient.get(`/api/auth/company/${companyCode}/settings`, {
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[OrganizationService] getCompanySettings error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Full Company Settings
 */
export const updateCompanySettings = async (companyCode, payload) => {
  try {
    const res = await apiClient.put(
      `/api/auth/company/${companyCode}/settings`,
      payload,
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] updateCompanySettings error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Company Address
 */
export const updateCompanyAddress = async (companyCode, address) => {
  try {
    const res = await apiClient.put(
      `/api/auth/company/${companyCode}/address`,
      { companyAddress: address, address },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] updateCompanyAddress error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Company Password
 */
export const updateCompanyPassword = async (companyCode, currentPassword, newPassword) => {
  try {
    const res = await apiClient.put(
      `/api/auth/company/${companyCode}/password`,
      { currentPassword, oldPassword: currentPassword, newPassword },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] updateCompanyPassword error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Team Size
 */
export const updateTeamSize = async (companyCode, teamSize) => {
  try {
    const res = await apiClient.put(
      `/api/auth/company/${companyCode}/team-size`,
      { teamSize },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] updateTeamSize error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Company Tags
 */
export const updateCompanyTags = async (companyCode, tags) => {
  try {
    const res = await apiClient.put(
      `/api/auth/company/${companyCode}/tags`,
      { tags },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] updateCompanyTags error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Submit Help & Support / RM Request
 */
export const submitSupportRequest = async (companyCode, payload) => {
  try {
    const res = await apiClient.post(
      `/api/auth/company/${companyCode}/rm-requests`,
      payload,
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] submitSupportRequest error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Get Subscription and Payment History
 */
export const getPaymentHistory = async (companyCode, params = {}) => {
  try {
    const res = await apiClient.get(`/api/payments/companies/${companyCode}/history`, {
      params,
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[OrganizationService] getPaymentHistory error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Create Renewal Order
 */
export const createRenewalOrder = async (companyCode, toDate) => {
  try {
    const res = await apiClient.post(
      "/api/payments/renewals",
      { companyCode, toDate },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] createRenewalOrder error:", err);
    return { success: false, message: err.response?.data?.message || err.message };
  }
};

/**
 * Verify Renewal Payment
 */
export const verifyRenewalPayment = async (payload) => {
  try {
    const res = await apiClient.post(
      "/api/payments/renewal-verifications",
      payload,
      { headers: payload.companyCode ? { "x-active-company-code": payload.companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] verifyRenewalPayment error:", err);
    return { success: false, message: err.response?.data?.message || err.message };
  }
};

/**
 * Get Proposal Design Templates
 */
export const getProposalTemplates = async (companyCode) => {
  try {
    const res = await apiClient.get("/api/auth/proposals", {
      params: { companyCode },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[OrganizationService] getProposalTemplates error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Get Specific Proposal Template by ID
 */
export const getProposalTemplateById = async (companyCode, id) => {
  try {
    const res = await apiClient.get(`/api/auth/proposals/${id}`, {
      params: { companyCode },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[OrganizationService] getProposalTemplateById error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Create or Update Proposal Template
 */
export const saveProposalTemplate = async (companyCode, templateData) => {
  try {
    const isEdit = !!templateData._id;
    const url = isEdit
      ? `/api/auth/proposals/${templateData._id}`
      : "/api/auth/proposals";

    const config = {
      timeout: 120000,
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    };

    const res = isEdit
      ? await apiClient.put(url, { ...templateData, companyCode }, config)
      : await apiClient.post(url, { ...templateData, companyCode }, config);

    return res;
  } catch (err) {
    console.error("[OrganizationService] saveProposalTemplate error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Delete Proposal Template
 */
export const deleteProposalTemplate = async (companyCode, id) => {
  try {
    const res = await apiClient.delete(`/api/auth/proposals/${id}`, {
      params: { companyCode },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[OrganizationService] deleteProposalTemplate error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Request Relationship Manager
 */
export const requestRelationshipManager = async (companyCode) => {
  try {
    const res = await apiClient.post(
      `/api/auth/company/${companyCode}/request-rm`,
      {},
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] requestRelationshipManager error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Setup Admin 2FA (generate secret & QR code)
 */
export const setupAdmin2FA = async (companyCode) => {
  try {
    const res = await apiClient.post(
      `/api/auth/company/${companyCode}/2fa/setup`,
      {},
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] setupAdmin2FA error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Verify & Enable Admin 2FA Setup
 */
export const verifyAdmin2FASetup = async (companyCode, token) => {
  try {
    const res = await apiClient.post(
      `/api/auth/company/${companyCode}/2fa/verify-setup`,
      { token: String(token).trim() },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] verifyAdmin2FASetup error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Disable Admin 2FA
 */
export const disableAdmin2FA = async (companyCode) => {
  try {
    const res = await apiClient.post(
      `/api/auth/company/${companyCode}/2fa/disable`,
      {},
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] disableAdmin2FA error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Company Profile Photo
 */
export const updateCompanyProfilePhoto = async (companyCode, profilePhoto) => {
  try {
    const res = await apiClient.put(
      `/api/auth/company/${companyCode}/profile-photo`,
      { profilePhoto },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[OrganizationService] updateCompanyProfilePhoto error:", err);
    return { success: false, message: err.message };
  }
};


